/**
 * 📋 SDLC 6.5.0 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/pages/TrainingPortal.tsx
 * PURPOSE: Training Portal — course catalog, enrollment, assessment, certifications
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * DATE: 2026-04-01 → 2026-09-04 (mobile app-shell parity + back-target fix)
 * COMPLIANCE: SDLC 6.5.0 + Zero-Mock Tolerance + Vietnamese Localization
 * PILLAR: P1 (Stage 06 - BUILD) + P6 (UI/UX Design)
 * SPRINT GOVERNANCE: Rule 3: Zero-Mock Tolerance
 *
 * 2026-09-04 (@coder, mobile app-shell parity — pilot-user report):
 * this page imported the SHARED `./ShiftSwapModern.module.css`, still on the
 * old max-width:1400px/900px desktop layout, so `/training` rendered
 * full-bleed wide while every other employee-flow screen is a 480px centered
 * app-shell. That shared file is also imported by ShiftSwapModern.tsx,
 * KPISelfAssessment.tsx and KPIManagerReview.tsx — the last of which is a
 * MANAGER-persona surface outside this pass's mandate — so it was NOT edited;
 * this page got its own `./TrainingPortal.module.css` instead (same
 * correct-blast-radius fix applied earlier this session to
 * LeaveRequestModern.tsx). Header also switched to the single-row icon-back
 * pattern, and the inline hardcoded-hex style objects moved into the
 * stylesheet as design-token classes.
 *
 * @business-context
 * - Domain: Multi-Industry BPM, Employee Training & Certification Management
 * - Impact: Revenue Critical — skill development, compliance training, certification tracking
 * - Stakeholders: Service Staff (NQH), Branch Managers, HR Team, Training Coordinators
 *
 * @technical-architecture
 * - Layer: Presentation Layer, Page Components
 * - Pattern: React Router, Tab-Based Layout, Component Composition
 * - Dependencies: React 18, TypeScript, lucide-react, TrainingPortal.module.css
 *
 * @cross-references
 * - Design: docs/02-design/09-ai-architecture/PROCESS-INTELLIGENCE-HEALTHCARE-TRAINING-DESIGN.md
 * - API: backend/app/api/v2/training/ (Training Portal v2 endpoints)
 * - Related: frontend/src/pages/LeaveRequestModern.tsx (pattern reference)
 * - Styles: frontend/src/pages/TrainingPortal.module.css
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import styles from './TrainingPortal.module.css';

/* ── Interfaces ────────────────────────────────────────────────────────────── */

interface Course {
  id: string;
  course_name_vi: string;
  course_name_en?: string;
  category: string;
  difficulty: string;
  duration_hours: number;
  industry_code: string;
  description_vi?: string;
  is_active: boolean;
}

interface Enrollment {
  id: string;
  course_id: string;
  employee_id: string;
  course_name_vi: string;
  status: string;
  enrolled_at: string;
  completed_at?: string;
  remaining_attempts: number;
  max_attempts: number;
  pass_threshold: number;
}

interface AssessmentResult {
  enrollment_id: string;
  score: number;
  passed: boolean;
  certification_code?: string;
  certification_name_vi?: string;
  attempts_remaining: number;
}

interface Certification {
  id: string;
  certification_code: string;
  certification_name_vi: string;
  issued_at: string;
  valid_until: string;
  is_expired: boolean;
  course_name_vi?: string;
}

/* ── Constants ─────────────────────────────────────────────────────────────── */

type TabKey = 'courses' | 'assessment' | 'certifications';

const TAB_LABELS: Record<TabKey, string> = {
  courses: 'Khóa học',
  assessment: 'Kiểm tra',
  certifications: 'Chứng chỉ',
};

const CATEGORY_LABELS: Record<string, string> = {
  SAFETY: 'An toàn',
  TECHNICAL: 'Kỹ thuật',
  SOFT_SKILL: 'Kỹ năng mềm',
  COMPLIANCE: 'Tuân thủ',
  MANAGEMENT: 'Quản lý',
  CUSTOMER_SERVICE: 'DVKH',
  SPECIALIZED: 'Chuyên môn',
};

const DIFFICULTY_LABELS: Record<string, string> = {
  BEGINNER: 'Cơ bản',
  INTERMEDIATE: 'Trung cấp',
  ADVANCED: 'Nâng cao',
};

const ENROLLMENT_STATUS_LABELS: Record<string, string> = {
  ENROLLED: 'Đã đăng ký',
  COMPLETED: 'Hoàn thành',
  FAILED: 'Không đạt',
};

const ALL_CATEGORIES = [
  { value: '', label: 'Tất cả danh mục' },
  { value: 'SAFETY', label: 'An toàn' },
  { value: 'TECHNICAL', label: 'Kỹ thuật' },
  { value: 'SOFT_SKILL', label: 'Kỹ năng mềm' },
  { value: 'COMPLIANCE', label: 'Tuân thủ' },
  { value: 'MANAGEMENT', label: 'Quản lý' },
  { value: 'CUSTOMER_SERVICE', label: 'DVKH' },
  { value: 'SPECIALIZED', label: 'Chuyên môn' },
];

/* ── Auth Helper ───────────────────────────────────────────────────────────── */

function getAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  try {
    const token = localStorage.getItem('access_token');
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const raw = localStorage.getItem('auth-storage');
    if (raw) {
      const parsed = JSON.parse(raw);
      const tenantKey = parsed?.state?.tenantKey || parsed?.state?.tenant_key;
      if (tenantKey) headers['X-Tenant-Key'] = tenantKey;
    }
  } catch { /* ignore */ }
  return headers;
}

function getEmployeeId(): string {
  try {
    const raw = localStorage.getItem('auth-storage');
    if (raw) {
      const parsed = JSON.parse(raw);
      return parsed?.state?.user?.id || parsed?.state?.employee_id || '';
    }
  } catch { /* ignore */ }
  return '';
}

function daysBetween(dateStr: string): number {
  const target = new Date(dateStr);
  const now = new Date();
  const diffMs = target.getTime() - now.getTime();
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
}

/* ── Component ─────────────────────────────────────────────────────────────── */

export const TrainingPortal: React.FC = () => {
  const navigate = useNavigate();

  // Tab state
  const [activeTab, setActiveTab] = useState<TabKey>('courses');

  // Data state
  const [courses, setCourses] = useState<Course[]>([]);
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [certifications, setCertifications] = useState<Certification[]>([]);

  // UI state
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [enrollingCourseId, setEnrollingCourseId] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState('');

  // Assessment state
  const [assessingEnrollmentId, setAssessingEnrollmentId] = useState<string | null>(null);
  const [assessmentScore, setAssessmentScore] = useState('');
  const [submittingAssessment, setSubmittingAssessment] = useState(false);
  const [assessmentResult, setAssessmentResult] = useState<AssessmentResult | null>(null);

  /* ── API Calls ──────────────────────────────────────────────────────────── */

  const loadCourses = useCallback(async () => {
    try {
      const res = await fetch('/api/v2/training/courses?industry_code=FB', {
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        const items = data.courses || data.items || data.data || [];
        setCourses(Array.isArray(items) ? items : []);
      } else {
        setCourses([]);
      }
    } catch {
      setCourses([]);
    }
  }, []);

  const loadEnrollments = useCallback(async () => {
    const employeeId = getEmployeeId();
    if (!employeeId) return;
    try {
      const res = await fetch(`/api/v2/training/employees/${employeeId}/enrollments`, {
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        const items = data.enrollments || data.items || data.data || [];
        setEnrollments(Array.isArray(items) ? items : []);
      } else {
        setEnrollments([]);
      }
    } catch {
      setEnrollments([]);
    }
  }, []);

  const loadCertifications = useCallback(async () => {
    const employeeId = getEmployeeId();
    if (!employeeId) return;
    try {
      const res = await fetch(`/api/v2/training/employees/${employeeId}/certifications`, {
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        const items = data.certifications || data.items || data.data || [];
        setCertifications(Array.isArray(items) ? items : []);
      } else {
        setCertifications([]);
      }
    } catch {
      setCertifications([]);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([loadCourses(), loadCertifications(), loadEnrollments()]);
      setLoading(false);
    };
    init();
  }, [loadCourses, loadCertifications, loadEnrollments]);

  /* ── Enrollment Handler ─────────────────────────────────────────────────── */

  const handleEnroll = async (courseId: string) => {
    setEnrollingCourseId(courseId);
    setError(null);
    setMessage(null);

    const employeeId = getEmployeeId();
    if (!employeeId) {
      setError('Không tìm thấy thông tin nhân viên. Vui lòng đăng nhập lại.');
      setEnrollingCourseId(null);
      return;
    }

    try {
      const res = await fetch('/api/v2/training/enrollments', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ course_id: courseId, employee_id: employeeId }),
      });

      const data = await res.json().catch(() => ({}));

      if (res.ok || res.status === 201) {
        setMessage('Đã đăng ký khóa học thành công.');
        await Promise.all([loadCourses(), loadEnrollments()]);
      } else {
        const rawDetail = data?.detail;
        const errMsg =
          (typeof rawDetail === 'string' ? rawDetail : rawDetail?.message_vi || rawDetail?.message) ||
          data?.message_vi ||
          data?.message ||
          'Không thể đăng ký khóa học.';
        setError(typeof errMsg === 'string' ? errMsg : JSON.stringify(errMsg));
      }
    } catch {
      setError('Lỗi kết nối. Vui lòng thử lại.');
    } finally {
      setEnrollingCourseId(null);
    }
  };

  /* ── Assessment Handler ─────────────────────────────────────────────────── */

  const handleSubmitAssessment = async (enrollmentId: string) => {
    const scoreNum = parseInt(assessmentScore, 10);
    if (isNaN(scoreNum) || scoreNum < 0 || scoreNum > 100) {
      setError('Điểm phải từ 0 đến 100.');
      return;
    }

    setSubmittingAssessment(true);
    setError(null);
    setMessage(null);
    setAssessmentResult(null);

    try {
      const res = await fetch(`/api/v2/training/assessments/${enrollmentId}/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ score: scoreNum, answers: [] }),
      });

      const data = await res.json().catch(() => ({}));

      if (res.ok) {
        const result: AssessmentResult = {
          enrollment_id: enrollmentId,
          score: data.score ?? scoreNum,
          passed: data.passed ?? scoreNum >= 70,
          certification_code: data.certification_code,
          certification_name_vi: data.certification_name_vi,
          attempts_remaining: data.attempts_remaining ?? 0,
        };
        setAssessmentResult(result);

        if (result.passed) {
          setMessage(`Chúc mừng! Bạn đã đạt ${result.score} điểm. Chứng chỉ đã được cấp.`);
        } else {
          setMessage(`Kết quả: ${result.score} điểm. Chưa đạt (yêu cầu ≥70%). Còn ${result.attempts_remaining} lượt thi.`);
        }

        setAssessingEnrollmentId(null);
        setAssessmentScore('');
        await Promise.all([loadEnrollments(), loadCertifications()]);
      } else {
        const rawDetail = data?.detail;
        const errMsg =
          (typeof rawDetail === 'string' ? rawDetail : rawDetail?.message_vi || rawDetail?.message) ||
          data?.message_vi ||
          data?.message ||
          'Không thể nộp bài kiểm tra.';
        setError(typeof errMsg === 'string' ? errMsg : JSON.stringify(errMsg));
      }
    } catch {
      setError('Lỗi kết nối. Vui lòng thử lại.');
    } finally {
      setSubmittingAssessment(false);
    }
  };

  /* ── Filtered Courses ───────────────────────────────────────────────────── */

  const filteredCourses = categoryFilter
    ? courses.filter(c => c.category === categoryFilter)
    : courses;

  /* ── Render ─────────────────────────────────────────────────────────────── */

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.headerContent}>
          {/* 2026-09-04 (BUG THẬT, cùng mẫu đã vá 5 nơi khác trong phiên này):
              nút này từng trỏ `/dashboard` — đó là `<DashboardGuard />`
              (App.tsx:405), guard tự kiểm tra hạn JWT phía client rồi đẩy ra
              `/login` khi access-token 15 phút đã hết hạn ⇒ nhân viên ngồi lâu
              bấm "quay lại" bị văng ra màn ĐĂNG NHẬP. Đích đúng ở đây là
              `/utilities` (App.tsx:661) — chính là tile "Đào tạo"
              (Utilities.tsx:191) điều hướng VÀO `/training`, nên nó là cha ngữ
              nghĩa (cùng lập luận đã áp dụng cho `/tasks` trong phiên này). */}
          <button
            type="button"
            onClick={() => navigate('/utilities')}
            className={styles.headerBackButton}
            aria-label="Quay lại"
            data-testid="training-header-back"
          >
            <ChevronLeft size={24} aria-hidden="true" />
          </button>
          <div className={styles.headerTitleWrap}>
            <h1 id="training-portal-title" className={styles.title}>Đào Tạo &amp; Chứng Chỉ</h1>
          </div>
        </div>
      </header>

      <main aria-labelledby="training-portal-title" className={styles.main}>

        {/* Tab navigation */}
        <div className={styles.actionsRow}>
          {(Object.keys(TAB_LABELS) as TabKey[]).map(tab => (
            <button
              key={tab}
              onClick={() => {
                setActiveTab(tab);
                setError(null);
                setMessage(null);
                setAssessmentResult(null);
              }}
              className={activeTab === tab ? styles.primaryButton : styles.secondaryButton}
              data-testid={`tab-${tab}`}
            >
              {TAB_LABELS[tab]}
            </button>
          ))}
        </div>

        {/* Status messages */}
        {message && (
          <p
            className={styles.statusSuccess}
            data-testid="training-success-msg"
            role="alert"
            aria-live="polite"
          >
            {message}
          </p>
        )}
        {error && (
          <p className={styles.statusError} data-testid="training-error-msg">{error}</p>
        )}

        {/* Loading */}
        {loading && (
          <div className={styles.card}>
            <p className={styles.cardDescription}>Đang tải dữ liệu...</p>
          </div>
        )}

        {/* ── TAB 1: Courses ──────────────────────────────────────────────── */}
        {!loading && activeTab === 'courses' && (
          <div className={styles.card}>
            <h2 className={styles.cardTitle}>Danh sách khóa học</h2>

            {/* Category filter */}
            <div className={styles.formGroup}>
              <label htmlFor="category-filter" className={styles.label}>
                Lọc theo danh mục
              </label>
              <select
                id="category-filter"
                value={categoryFilter}
                onChange={e => setCategoryFilter(e.target.value)}
                className={styles.input}
                data-testid="category-filter"
              >
                {ALL_CATEGORIES.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>
            </div>

            {filteredCourses.length === 0 ? (
              <p className={styles.cardDescription}>
                Không có khóa học nào{categoryFilter ? ` trong danh mục "${CATEGORY_LABELS[categoryFilter] || categoryFilter}"` : ''}.
              </p>
            ) : (
              <ul className={styles.requestList}>
                {filteredCourses.map(course => (
                  <li
                    key={course.id}
                    className={styles.requestItem}
                    data-testid={`course-${course.id}`}
                  >
                    <div className={styles.requestHeader}>
                      <span className={styles.statusBadge} data-status="APPROVED">
                        {DIFFICULTY_LABELS[course.difficulty] || course.difficulty}
                      </span>
                      <span className={styles.requestDate}>
                        {course.duration_hours} giờ
                      </span>
                    </div>
                    <p className={styles.requestReason}>
                      {course.course_name_vi}
                    </p>
                    <div className={styles.requestFooter}>
                      <span className={styles.requestTime}>
                        <span
                          className={`${styles.statusBadge} ${styles.badgeSmall}`}
                          data-status="MANAGER_REVIEW"
                        >
                          {CATEGORY_LABELS[course.category] || course.category}
                        </span>
                        <span
                          className={`${styles.statusBadge} ${styles.badgeSmall}`}
                          data-status="PENDING"
                        >
                          {course.industry_code}
                        </span>
                      </span>
                      <button
                        onClick={() => handleEnroll(course.id)}
                        disabled={enrollingCourseId === course.id}
                        className={styles.listButton}
                        data-testid={`enroll-${course.id}`}
                      >
                        {enrollingCourseId === course.id ? 'Đang đăng ký...' : 'Đăng ký'}
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* ── TAB 2: Assessment ───────────────────────────────────────────── */}
        {!loading && activeTab === 'assessment' && (
          <div className={styles.card}>
            <h2 className={styles.cardTitle}>Kiểm tra đánh giá</h2>

            {/* Assessment result display */}
            {assessmentResult && (
              <div
                className={styles.assessmentResult}
                data-passed={assessmentResult.passed ? 'true' : 'false'}
                data-testid="assessment-result"
              >
                <strong>{assessmentResult.passed ? 'Đạt' : 'Không đạt'}</strong>
                {' — '}Điểm: {assessmentResult.score}/100
                {assessmentResult.passed && assessmentResult.certification_name_vi && (
                  <span> — Chứng chỉ: {assessmentResult.certification_name_vi} ({assessmentResult.certification_code})</span>
                )}
                {!assessmentResult.passed && (
                  <span> — Còn {assessmentResult.attempts_remaining} lượt thi</span>
                )}
              </div>
            )}

            {enrollments.length === 0 ? (
              <p className={styles.cardDescription}>
                Chưa đăng ký khóa học nào. Vui lòng đăng ký khóa học trước.
              </p>
            ) : (
              <ul className={styles.requestList}>
                {enrollments.map(enrollment => (
                  <li
                    key={enrollment.id}
                    className={styles.requestItem}
                    data-testid={`enrollment-${enrollment.id}`}
                  >
                    <div className={styles.requestHeader}>
                      <span
                        className={styles.statusBadge}
                        data-status={enrollment.status === 'COMPLETED' ? 'APPROVED' : enrollment.status === 'FAILED' ? 'REJECTED' : 'PENDING'}
                      >
                        {ENROLLMENT_STATUS_LABELS[enrollment.status] || enrollment.status}
                      </span>
                      <span className={styles.requestDate}>
                        Đăng ký: {new Date(enrollment.enrolled_at).toLocaleDateString('vi-VN')}
                      </span>
                    </div>
                    <p className={styles.requestReason}>
                      {enrollment.course_name_vi}
                    </p>
                    <p className={styles.requestPeer}>
                      Còn {enrollment.remaining_attempts} lượt thi — Điểm đạt: ≥{enrollment.pass_threshold || 70}%
                    </p>

                    {/* Assessment form inline */}
                    {enrollment.status === 'ENROLLED' && enrollment.remaining_attempts > 0 && (
                      <div className={styles.requestFooter}>
                        {assessingEnrollmentId === enrollment.id ? (
                          <div className={styles.scoreRow}>
                            <input
                              type="number"
                              min={0}
                              max={100}
                              value={assessmentScore}
                              onChange={e => setAssessmentScore(e.target.value)}
                              placeholder="Điểm (0-100)"
                              className={`${styles.input} ${styles.scoreInput}`}
                              data-testid={`score-input-${enrollment.id}`}
                            />
                            <button
                              onClick={() => handleSubmitAssessment(enrollment.id)}
                              disabled={submittingAssessment}
                              className={styles.listButton}
                              data-testid={`submit-assessment-${enrollment.id}`}
                            >
                              {submittingAssessment ? 'Đang nộp...' : 'Nộp bài'}
                            </button>
                            <button
                              onClick={() => {
                                setAssessingEnrollmentId(null);
                                setAssessmentScore('');
                              }}
                              className={styles.cancelButton}
                            >
                              Hủy
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => {
                              setAssessingEnrollmentId(enrollment.id);
                              setAssessmentScore('');
                              setAssessmentResult(null);
                              setError(null);
                              setMessage(null);
                            }}
                            className={styles.listButton}
                            data-testid={`start-assessment-${enrollment.id}`}
                          >
                            Làm bài kiểm tra
                          </button>
                        )}
                      </div>
                    )}

                    {enrollment.status === 'COMPLETED' && (
                      <div className={styles.requestFooter}>
                        <span className={styles.requestTime}>
                          Hoàn thành: {enrollment.completed_at ? new Date(enrollment.completed_at).toLocaleDateString('vi-VN') : '—'}
                        </span>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* ── TAB 3: Certifications ───────────────────────────────────────── */}
        {!loading && activeTab === 'certifications' && (
          <div className={styles.card}>
            <h2 className={styles.cardTitle}>Chứng chỉ của tôi</h2>

            {certifications.length === 0 ? (
              <p className={styles.cardDescription}>
                Chưa có chứng chỉ nào. Hoàn thành khóa học và vượt qua bài kiểm tra để nhận chứng chỉ.
              </p>
            ) : (
              <ul className={styles.requestList}>
                {certifications.map(cert => {
                  const daysRemaining = daysBetween(cert.valid_until);
                  return (
                    <li
                      key={cert.id}
                      className={styles.requestItem}
                      data-testid={`cert-${cert.id}`}
                    >
                      <div className={styles.requestHeader}>
                        <span
                          className={styles.statusBadge}
                          data-status={cert.is_expired ? 'REJECTED' : 'APPROVED'}
                        >
                          {cert.is_expired ? 'Hết hạn' : 'Còn hiệu lực'}
                        </span>
                        <span className={styles.requestDate}>
                          {cert.certification_code}
                        </span>
                      </div>
                      <p className={styles.requestReason}>
                        {cert.certification_name_vi}
                      </p>
                      {cert.course_name_vi && (
                        <p className={styles.requestPeer}>
                          Khóa học: {cert.course_name_vi}
                        </p>
                      )}
                      <div className={styles.requestFooter}>
                        <span className={styles.requestTime}>
                          Cấp: {new Date(cert.issued_at).toLocaleDateString('vi-VN')}
                          {' — '}
                          Hết hạn: {new Date(cert.valid_until).toLocaleDateString('vi-VN')}
                        </span>
                        {!cert.is_expired && (
                          <span
                            className={`${styles.statusBadge} ${styles.badgeSmall}`}
                            data-status="APPROVED"
                          >
                            Còn {daysRemaining} ngày
                          </span>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}

      </main>
    </div>
  );
};

export default TrainingPortal;
